import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { ResourceRequestStatus } from '~/types';
import RESOURCE_REQUESTS_ALLOCATIONS_QUERY from '~/modules/resourcing/common/fragments/resourceRequestsQuery';
import { tryLoadCachedResourceRequestsQuery } from '~/modules/resourcing/common/util';

export const BULK_DELETE_RESOURCE_REQUESTS = gql`
  mutation BulkDeleteResourceRequests(
    $input: BulkDeleteResourceRequestsInput!
  ) {
    bulkDeleteResourceRequests2(input: $input) {
      resourceRequestIds
    }
  }
`;

export const updateCache = ({ projectUri }) => (proxy, mutationResponse) => {
  const {
    data: {
      isOptimistic,
      bulkDeleteResourceRequests2: { resourceRequestIds }
    }
  } = mutationResponse;

  const cachedResourceRequests = tryLoadCachedResourceRequestsQuery({
    proxy,
    projectUri,
    isOptimistic
  });

  if (cachedResourceRequests) {
    const { resourceRequests: data } = cachedResourceRequests;

    proxy.writeQuery({
      query: RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
      variables: {
        projectUri,
        cursor: null,
        limit: 500
      },
      data: {
        resourceRequests: {
          ...data,
          resourceRequests: data.resourceRequests.filter(
            c => !resourceRequestIds.includes(c.id)
          )
        }
      }
    });
  }
};

export const useBulkDeleteResourceRequestsFromProject = () => {
  const [bulkDeleteResourceRequests] = useMutation(
    BULK_DELETE_RESOURCE_REQUESTS
  );

  return useCallback(
    ({ project: { id }, resourceRequests }) => {
      const resourceRequestIds = resourceRequests
        .filter(
          r =>
            r.requestStatus !== ResourceRequestStatus.Tentative &&
            r.requestStatus !== ResourceRequestStatus.Complete
        )
        .map(c => c.id);

      bulkDeleteResourceRequests({
        variables: {
          input: {
            resourceRequestIds
          }
        },
        optimisticResponse: {
          __typename: 'Mutation',
          isOptimistic: true,
          bulkDeleteResourceRequests2: {
            __typename: 'BulkDeleteResourceRequestsResult',
            resourceRequestIds
          }
        },
        update: updateCache({
          projectUri: id
        })
      });
    },
    [bulkDeleteResourceRequests]
  );
};

export default useBulkDeleteResourceRequestsFromProject;
